import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { styled, useTheme } from '@mui/material/styles';
import { BrowserRouter as Router, Route, Switch, Redirect, useLocation, useHistory } from 'react-router-dom';
import MuiAlert from '@mui/material/Alert';
import _ from 'lodash';
import * as Sentry from "@sentry/react";
import { LABELGENAPI } from './API';

// mui libraries
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';

// Auth
import LoginPage from './pages/Auth/LoginPage';
import RegisterPage from './pages/Auth/RegisterPage';
import PasswordresetPage from './pages/Auth/PasswordresetPage';
import UserVerifyPage from './pages/Auth/UserVerifyPage';
import NotFoundPage from './pages/Auth/NotFoundPage';

// Dashboard
import DashboardPage from './pages/Dashboard/DashboardPage';

// Claims
import ClaimsPage from './pages/Claims/ClaimsPage';

// Analytics
import AnalyticsPage from './pages/Analytics/AnalyticsPage';

// Orders
import OrdersPage from './pages/Orders/OrdersPage';
import OrdersNewPage from './pages/Orders-new/OrdersNewPage';
import NewShipmentsPage from './pages/Shipments/NewShipment/NewShipmentsPage';
import QuickShipmentsPage from './pages/Shipments/QuickOrder/QuickShipmentsPage';
import BulkOrdersPage from './pages/BulkOrder/BulkOrderPage';

// Reports
import ManifestDetailsPage from './pages/ManifestDetail/ManifestDetailsPage';
import OrderDetailsPage from './pages/OrderDetail/OrderDetailsPage';
import GroupedManifestPage from './pages/Manifest/GroupedManifestsPage';
import DeliveriesPage from './pages/Reports/Deliveries/DeliveriesPage';
import DeliveriesMysqlPage from './pages/Reports/Deliveries/DeliveriesMysqlPage';
import DeparturePage from './pages/Reports/Departure/DeparturePage';
import ReturnDeparturePage from './pages/Reports/ReturnDeparture/ReturnDeparturePage';
import LinteitemsLogPage from './pages/Reports/InventoryLogs/LineItemsLogReport';
import FulfillmentsPage from './pages/Reports/Fulfillments/FulfillmentsLogReport';
import NotificationsDashboardPage from './pages/Reports/Notifications/NotificationsDashboardPage';
import CustomerFeedbackDashboardPage from './pages/Reports/Feedback/CustomerFeedbackDashboardPage';
import WidgetAnalyticsDashboardPage from './pages/Reports/Widgets/WidgetAnalyticsDashboardPage';
import ManifestsPage from './pages/Reports/Manifest/ManifestsPage';
import PickupPage from './pages/Reports/Pickup/PickupPage';
import ShipFromSavingsPage from './pages/Reports/ShipFromSavings/ShipFromSavingsPage';

import OrderPicklistPage from './pages/OrderPicklist/OrderPicklistPage';
import DimUpdatePage from './pages/DimUpdate/DimUpdatePage';

// Integrations
import IntegrationPage from './pages/Integration/Marketplace/IntegrationPage';
import CarrierCredentialsPage from './pages/Integration/Carrier/CarrierCredentialsPage';
import NotificationPage from './pages/Integration/Notification/IntegrationPage';
import NotificationTemplatePage from './pages/Integration/Notification/NotificationTemplatePage';
// Settings
import SettingsPage from './pages/Settings/SettingsPage';

import ProductLineItemsPage from "./pages/Settings/ProductLineItems/ProductLineItemsPage";

// components
import Header from './pages/General/Header';
import Sidebar from './pages/General/Sidebar';
import Footer from './pages/General/Footer';
import Notification from './pages/General/Notification'
import Claims from './pages/General/Claims'

// slice
import { authenticate, verifyAuthFailure } from './pages/Auth/authSlice';
import { setQzStateInfo, setQzNotifications, resetQzNotifications, setQzInProgress } from './qzSlice';
// css
import './App.css';

const qz = require("qz-tray");

const verifyQzSecuritySignature = () => {
  console.log("Verifying qz signature")
  qz.security.setCertificatePromise((resolve, reject) => {
    LABELGENAPI.
      get("/security/digital-certificate").
      then(function (response) {
        resolve(response.data)
      })
      .catch(function (error) {
        console.log(error.response)
        reject(error.response)
      });
  });
  qz.security.setSignatureAlgorithm("SHA512");
  qz.security.setSignaturePromise((toSign) => {
    return (resolve, reject) => {
      LABELGENAPI.
        get("/security/private-key?data=" + toSign).
        then(function (response) {
          resolve(response.data)
        })
        .catch(function (error) {
          console.log(error.response)
          reject(error.response)
        });
    };
  });
}

const requestQZConnect = (dispatch, manualCall = false) => {
  dispatch(setQzInProgress())
  verifyQzSecuritySignature();
  qz.websocket.connect().then(() => {
    dispatch(setQzStateInfo({
      "is_active": true,
      "qz": qz,
      "printer": ""
    }))
    qz.websocket.setClosedCallbacks(() => {
      dispatch(setQzStateInfo({
        "is_active": false,
        "printer": "",
        "qz": ""
      }))
    })
    return qz.printers.getDefault();
  }).then((printer) => {
    if (printer !== "undefined" && typeof printer !== "undefined" && typeof printer == "string" && printer.length != 0) {
      dispatch(setQzStateInfo({
        "is_active": true,
        "printer": printer,
        "qz": qz
      }))
    }
  }).catch((e) => {
    console.log(e.message)
    if (e.message === "Unable to establish connection with QZ") {
      dispatch(setQzStateInfo({
        "is_active": false,
        "printer": "",
        "qz": ""
      }))
      if (manualCall == true) {
        dispatch(setQzNotifications({
          "message": [e.message],
          "error": true
        }))
      }
    } else if (e.message === "An open connection with QZ Tray already exists") {
      qz.printers.getDefault().then(function (printer) {
        if (printer !== "undefined" && typeof printer !== "undefined" && typeof printer == "string" && printer.length != 0) {
          dispatch(setQzStateInfo({
            "is_active": true,
            "printer": printer,
            "qz": qz
          }))
        }
      })
    }
  })
  return true
}

const requestQZDisconnect = (dispatch, qzState) => {
  qzState.qz.websocket.disconnect().then(() => {
    console.log("Disconnected")
  }).catch((e) => {
    console.log(e.message);
    dispatch(setQzNotifications({
      "message": [e.message],
      "error": true
    }))
  })
}

const App = ({ }) => {
  const { REACT_APP_DEBUG } = process.env;

  const auth = useSelector(state => state.auth);
  const qzState = useSelector(state => state.qz);
  const profile = useSelector(state => state.auth.profile);
  const dispatch = useDispatch();
  const locations = useLocation();
  const currentPageActive = locations.pathname.replace('/', '');
  const [redirectStatus,setRedirectStatus]=useState(false)

    useEffect(() => {
        const publicRoutes = ['login', 'register', 'passwordreset'];
        const currentPath = locations.pathname.split('/')[1] || '';
        const isPublicAuthPage = publicRoutes.includes(currentPath);
        const storedRoles = localStorage.getItem('roles');

        if (isPublicAuthPage) {
            if (storedRoles) {
                // 1. Session exists in storage! Fetch profile to restore auth.isLoggedIn = true.
                // React Router's <Redirect to="/dashboard"/> will then automatically push them to dashboard.
                dispatch(authenticate());
            } else {
                // 2. Truly no session exists -> clear auth state for public page.
                dispatch(verifyAuthFailure());
            }
        } else if (!auth.isLoggedIn) {
            if (storedRoles) {
                // 3. User is on protected route (e.g., /orders-new/all) -> verify/hydrate session.
                dispatch(authenticate());
            } else {
                // 4. User is on protected route with NO session -> force logout to login page.
                dispatch(verifyAuthFailure());
            }
        }

        requestQZConnect(dispatch);
    }, [dispatch]);

  useEffect(() => { //Tawk Chat Widget
    setRedirectStatus(false)
    if (!_.isUndefined(REACT_APP_DEBUG) && REACT_APP_DEBUG == "false") {
      Sentry.setUser({
        name: profile && profile.name,
        email: profile && profile.email,
        source: (!_.isUndefined(profile?.source)) ? profile.source : ""
      });

      if (!_.isUndefined(profile) && !_.isUndefined(profile?.name) && !_.isUndefined(profile?.email)) {
        window.dataLayer.push({'dl_user_id': profile.id });
        console.log("data layer pused id")
        console.log("Current values of the Data Layer:", window.dataLayer);
      }

      if (!document.getElementById('tawkId')) {
        const script = document.createElement("script");
        script.id = 'zsiqscript';
        script.async = true;
        script.src = 'https://embed.tawk.to/602563009c4f165d47c26567/1eu90lf34';
        script.charset = 'UTF-8';
        script.setAttribute('crossorigin', '*');
        const first_script_tag = document.getElementsByTagName("script")[0];
        first_script_tag.parentNode.insertBefore(script, first_script_tag)
        window.Tawk_API = window.Tawk_API || {};
        window.Tawk_API.customStyle = {
          visibility: {
            desktop: {
              position: 'bl',
              xOffset: 10,
              yOffset: 50
            }
          }
        }
      }
      if (!_.isUndefined(profile) && !_.isUndefined(profile?.name) && !_.isUndefined(profile?.email)) {
        window.dataLayer.push({'dl_user_id': profile.id });
        console.log("data layer pused id")
        window.Tawk_API = window.Tawk_API || {};
        window.Tawk_LoadStart = new Date();
        window.Tawk_API.onLoad = function () {
          if (_.isFunction(window.Tawk_API.setAttributes)) {
            window.Tawk_API.setAttributes({
              name: profile.name,
              email: profile.email
            }, function (error) { });
          }
        }
      }
     
      if (_.isFunction(window.Autopilot?.run)) {
        let splittedName = _.split(profile && profile?.name, ' ', 2);
        window.Autopilot.run("associate", {
          Email: profile && profile?.email,
          FirstName: (!_.isUndefined(splittedName[0])) ? splittedName[0] : "",
          LastName: (!_.isUndefined(splittedName[1])) ? splittedName[1] : "",
          Status: (!_.isUndefined(profile && profile?.status)) ? profile && profile.status : "",
          LeadSource: (!_.isUndefined(profile && profile?.source)) ? profile && profile.source : "",
          MailingCountry: (!_.isUndefined(profile && profile?.country)) ? profile && profile.country : "",
          custom: {
            "date--email_verified_at": (!_.isUndefined(profile && profile?.email_verified_at)) ? profile && profile.email_verified_at : "",
            "date--signup_date": (!_.isUndefined(profile && profile?.created_at)) ? profile && profile.created_at : "",
            "string--api_token": (!_.isUndefined(profile && profile?.api_token)) ? profile && profile.api_token : "",
            "integer--uid": (!_.isUndefined(profile && profile?.id)) ? profile && profile.id : "",
          }
        });
      }
    }
  }, [currentPageActive, profile])

  window.localStorage.setItem("roles", JSON.stringify(auth.loginLoading))
  const roles = () => {
    if (auth?.profile?.name) {
      window.localStorage.setItem("roles", JSON.stringify(auth.profile.name));
    }
  }
  const DrawerHeader = styled('div')(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    padding: theme.spacing(0, 1),
    ...theme.mixins.toolbar,
  }));

  const connectQZ = () => {
    console.log("Rechecking QZ")
    requestQZConnect(dispatch, true)
  }

  const disconnectQZ = () => {
    if (qzState.queue_count <= 0) {
      requestQZDisconnect(dispatch, qzState)
    } else {
      dispatch(setQzNotifications({
        "message": ["Please wait for pending queues to be printed"],
        "error": true
      }))
    }
  }

  const history = useHistory();
  const location = useLocation();

  const params = new URLSearchParams(location.search);
  let sessionVerification = new URLSearchParams(window.location.search).get("verification");
  const CheckParams = () => {
      useEffect(() => {
        if (redirectStatus===false && profile && profile.id ===94 && profile.id === profile.parent_id && (typeof profile.questioner_data==="undefined" || profile.verification_status!=="VERIFIED")) {
          history.push("/dashboard");
          setRedirectStatus(true)
        }
        if (redirectStatus===false && sessionVerification!==null) {
          history.push("/dashboard?verification=success");
          setRedirectStatus(true)
        }
      }, [history,redirectStatus,params]);
      return null;
  }
  return (
    <>
      <div>
        {(auth.logoutLoading === true || auth.loginLoading === true || auth.resetLoading === true) &&
          <div className="overlay-loader">
            <CircularProgress />
          </div>
        }

        {/* 1. AUTO REDIRECT LOGGED-IN USERS TO DASHBOARD WHEN ACCESSIBILITY AUTH PAGES */}
        {(auth.isLoggedIn === true && (currentPageActive === "login" || currentPageActive === "register" || currentPageActive === "passwordreset" || currentPageActive === "")) &&
          <Redirect to="/dashboard" />
        }

        {/* 2. RENDER REGISTER PAGE (UNAUTHENTICATED ONLY) */}
        {(auth.isLoggedIn === false && currentPageActive === "register") &&
          <RegisterPage />
        }

        {/* 3. RENDER LOGIN PAGE (UNAUTHENTICATED ONLY) */}
        {(auth.isLoggedIn === false && auth.loginLoading === false && auth.logoutLoading === false && currentPageActive !== "passwordreset" && currentPageActive !== "register") &&
          <>
            <Redirect to="/login" push />
            <LoginPage />
          </>
        }

        {/* 4. EMAIL VERIFICATION */}
        {(auth.isLoggedIn === true && auth.loginLoading === false && profile && profile.email_verified_at == null) &&
          <>
            <Redirect to="/userverify" push />
            <UserVerifyPage />
          </>
        }

        {/* 5. PASSWORD RESET (UNAUTHENTICATED ONLY) */}
        {(auth.isLoggedIn === false && currentPageActive === "passwordreset") &&
          <PasswordresetPage />
        }

        {/* 6. PROTECTED APP ROUTES */}
        {(auth.isLoggedIn === true && auth.profile && profile.email_verified_at !== null) && <>
          {(qzState && qzState.notification && qzState.notification.show) &&
            <Notification
              is_open={qzState.notification.show}
              messageType={qzState.notification.messageType}
              messages={qzState.notification.messageContent}
              handleClose={() => dispatch(resetQzNotifications())}
            />
          }
          <Box sx={{ display: 'flex' }} onLoad={roles}>
            <Sidebar currentPage={currentPageActive} connectQZ={connectQZ} disconnectQZ={disconnectQZ} />
            <Box component="main" sx={{ flexGrow: 1, p: 3, background: '#fcfcfc',width:'100%',overflow:'auto' }}>
              <DrawerHeader />
              <CheckParams />
              <Switch>
                <Redirect exact from="/" to="/dashboard" />
                <Redirect exact from="/register" to="/dashboard" />
                <Redirect exact from="/login" to="/dashboard" />
                <Redirect exact from="/orders" to="/orders/all" />
                <Redirect exact from="/integration" to="/integration/marketplace" />

                <Redirect exact from="/orders" to="/orders-new/all" />
                <Redirect exact from="/orders/all" to="/orders-new/all" />
                <Redirect exact from="/orders/open" to="/orders-new/open" />
                <Redirect exact from="/orders/return" to="/orders-new/return" />
                
                <Route path='/dashboard' component={DashboardPage} />
                <Route path='/orders/all' component={OrdersPage} />
                <Route path='/orders/open?report=openorders' component={OrdersPage} />
                <Route path='/orders/return?report=returnorders' component={OrdersPage} />
                <Route path='/orders-new/all' component={OrdersNewPage} />
                <Route path='/orders-new/open' component={OrdersNewPage} />
                <Route path='/orders-new/return' component={OrdersNewPage} />
                <Route path='/orders/new' component={NewShipmentsPage} />
                <Route path='/orders/quick' component={QuickShipmentsPage} />
                <Route path='/orders/bulk' component={BulkOrdersPage} />
                <Route path='/reports/fulfillments' component={FulfillmentsPage} />
                <Route path='/reports/lineitems-log' component={LinteitemsLogPage} />
                <Route path='/reports/savings' component={ShipFromSavingsPage} />
                <Route path='/orders/picklist' component={OrderPicklistPage} />
                {/* <Route path='/orders/:orderId' component={OrderDetailsPage} /> */}
                <Route path='/manifests/:manifestId' component={ManifestDetailsPage} />
                <Route path='/manifests' component={GroupedManifestPage} />
                <Route path='/claims' component={ClaimsPage} />
                <Route path='/reports/deliveries' component={DeliveriesMysqlPage} />
                <Route path='/reports/dim-update' component={DimUpdatePage} />
                <Route path='/reports/departure' component={DeparturePage} />
                <Route path='/reports/return-departure' component={ReturnDeparturePage} />
                <Route path='/reports/manifests' component={ManifestsPage} />
                <Route path='/reports/pickup' component={PickupPage} />
                <Route path='/reports/notifications' component={NotificationsDashboardPage} />
                <Route path='/reports/feedback' component={CustomerFeedbackDashboardPage} />
                <Route path='/reports/widget-chat' component={WidgetAnalyticsDashboardPage} />
                <Route path='/settings' render={(props) => (
                    <SettingsPage 
                      {...props} 
                      connectQZ={connectQZ} 
                      disconnectQZ={disconnectQZ} 
                    />
                  )} 
                />
                <Route path='/analytics' component={AnalyticsPage} />
                <Route path='/inventory' component={ProductLineItemsPage} />
                <Route path='/integration/marketplace' component={IntegrationPage} />
                {profile && (profile.is_reliable_user || profile.hide_carrier === false) && <Route path='/integration/carriercredentials' component={CarrierCredentialsPage} />}
                <Route path='/integration/notification' component={NotificationPage} />
                <Route path='/notification/template/:event_type/:notification_type/:is_enabled/:id' component={NotificationTemplatePage} />
                <Route path='*' component={NotFoundPage} />
              </Switch>
            </Box>
          </Box>
        </>
        }
      </div>
    </>
  )
};

export default App;